import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AlbumEntity, PhotoEntity, PhotosResponse } from '@interpals/api-web';

import api from 'api';

export const RECENT_PHOTOS_FETCH_LIMIT = 10;
export const ALBUMS_FETCH_LIMIT = 10;
export const ALL_ALBUMS_FETCH_LIMIT = 100;

export const fetchAlbums = createAsyncThunk<
    AlbumEntity[],
    {
        userId: string;
        offset?: number;
    }
>('fetch/albums', async ({ userId, offset }) => {
    return api
        .getUserAlbums({ id: userId, offset: offset ?? 0, limit: ALBUMS_FETCH_LIMIT, extra: 'album_cover_path' })
        .then((albumsResponse) =>
            albumsResponse.data.sort(
                (a: AlbumEntity, b: AlbumEntity): number => Number(b.profile_photos) - Number(a.profile_photos),
            ),
        );
});

export const fetchAllAlbums = createAsyncThunk<
    AlbumEntity[],
    {
        userId: string;
    }
>('fetch/all-albums', async ({ userId }) => {
    const fetchAlbumBatch = async () =>
        api
            .getUserAlbums({ id: userId, offset: 0, limit: ALL_ALBUMS_FETCH_LIMIT, extra: 'album_cover_path' })
            .then((response) => response.data);

    // Backend only lets us fetch 100 albums at a time, so we need to
    // fetch in batches until we get less than 100 albums back.
    let albums: AlbumEntity[] = [];
    let albumBatch: AlbumEntity[] = [];
    do {
        albumBatch = await fetchAlbumBatch();
        albums = [...albums, ...albumBatch];
    } while (albumBatch.length === ALL_ALBUMS_FETCH_LIMIT);

    return albums;
});

// ---- single album actions ----
export const fetchAlbum = createAsyncThunk<AlbumEntity, string, { rejectValue: Response }>(
    'fetch/album',
    async (id, { rejectWithValue }) => {
        try {
            return await api.getAlbum({ id, extra: 'photos' });
        } catch (e) {
            return rejectWithValue(e as Response);
        }
    },
);

export const fetchRecentPhotos = createAsyncThunk<
    PhotosResponse,
    {
        userId: string;
        offset?: number;
        limit?: number;
    }
>('fetch/recent-photos', async ({ userId, offset = 0, limit = RECENT_PHOTOS_FETCH_LIMIT }) => {
    return api.getUserPhotos({ id: userId, offset: offset, limit: limit });
});

export const fetchPhotoCount = createAsyncThunk<number, { userId: string }>('fetch/photo-count', async ({ userId }) => {
    return (await api.getTotalNumberOfUserPhotos({ id: userId })).total;
});

export const clearAlbum = createAction('clear/album');

export const deleteAlbum = createAsyncThunk<string, { id: string; uid: string }>('delete/album', async (params) => {
    const { id } = params;

    await api.deleteAlbum({ id });

    return id;
});

interface CreateAlbumParams {
    title: string;
    view_acl: 'users' | 'friends' | 'me';
    comment_acl: 'users' | 'friends' | 'me';
    description?: string;
}

export const createAlbum = createAsyncThunk<AlbumEntity, CreateAlbumParams>('create/album', async (params) => {
    return api.createAlbum({ ...params });
});

interface DeleteAlbumPhotoParams {
    photoId: string;
    // For use in reducers
    userId: string;
    albumId: string;
}

export const deleteAlbumPhoto = createAsyncThunk<DeleteAlbumPhotoParams, DeleteAlbumPhotoParams>(
    'album/photo/delete',
    async ({ photoId, userId, albumId }) => {
        (await api.deletePhoto({ id: photoId })).id;

        return { photoId, userId, albumId };
    },
);

interface MovePhotoParams {
    photoId: string;
    targetAlbumId: string;

    // For reducer
    sourceAlbumId: string;
    userId: string;
}

export const movePhotoToAlbum = createAsyncThunk<MovePhotoParams, MovePhotoParams>(
    'album/photo/move',
    async ({ photoId, targetAlbumId, sourceAlbumId, userId }) => {
        await api.updatePhoto({ id: photoId, album_id: targetAlbumId });

        return { photoId, targetAlbumId, sourceAlbumId, userId };
    },
);

interface UpdateAlbumParams {
    id: string;
    title: string;
    view_acl: 'users' | 'friends' | 'me';
    comment_acl: 'users' | 'friends' | 'me';
    description?: string;
}

export const updateAlbumDetails = createAsyncThunk<AlbumEntity, UpdateAlbumParams>('album/update', async (params) => {
    return await api.updateAlbum({ ...params });
});

interface AlbumPhotosOrdersUpdateParams {
    id: string;
    photo_list: string;
}

export const updateAlbumPhotosOrder = createAsyncThunk<AlbumEntity, AlbumPhotosOrdersUpdateParams>(
    'album/photos/order/update',
    async (params) => {
        return api.updateAlbum({ ...params });
    },
);

export const uploadPhotoToAlbumNew = createAsyncThunk<PhotoEntity, { file: File; albumId: string }>(
    'photo/uploadToAlbum',
    async ({ file, albumId }, { rejectWithValue }) => {
        const data = await api.createPhoto({ file, aid: albumId }).catch(async (error) => {
            return rejectWithValue(await error.json());
        });

        return data as unknown as PhotoEntity;
    },
);

export const moderationDeletePhoto = createAction<{
    photoId: string;
    // For use in reducers
    userId: string;
    albumId: string;
}>('delete/moderation/album/photo');

// Former photo/actions.ts

interface UpdatePhotoDescriptionParams {
    id: string;
    album_id: string;
    description?: string;
}

export const updatePhotoDescription = createAsyncThunk<PhotoEntity, UpdatePhotoDescriptionParams>(
    'photo/description/update',
    async ({ id, album_id, description }, { rejectWithValue }) => {
        try {
            return await api.updatePhoto({ id, album_id, description });
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

interface GetPhotoParams extends Record<string, string | undefined> {
    photoId: string;
    ownerUid: string;
}

export const fetchPhoto = createAsyncThunk<PhotoEntity, GetPhotoParams>(
    'photo/fetch',
    async ({ photoId, ownerUid }, { rejectWithValue }) => {
        try {
            return await api.getPhoto({ id: photoId, owner_id: ownerUid, extra: 'album' });
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

interface CropThumbParams {
    id: string;
    x: number;
    y: number;
    length: number;
}

export const cropThumb = createAsyncThunk<PhotoEntity, CropThumbParams>(
    'photo/thumb/crop',
    async ({ id, x, y, length }) => {
        return await api.cropThumb({ id, x, y, length });
    },
);
